import React from 'react'

import { Trans } from '@disco/i18n/trans'
import type { TFunction } from '@disco/i18n/use-translation'

import InlineLink from 'components/ui/inline-link'

export type AlbumVariant = 'albums' | 'releases'

/**
 * Labels and messages for managing a catalog's albums, which artist profiles call releases.
 * Each variant has its own keys rather than an interpolated noun, so translations
 * can follow each language's grammar.
 * @param t - Translation function bound to the `catalogs-management` namespace
 * @param variant - `albums` for catalogs, `releases` for artist profiles
 */
export const getAlbumLabels = (t: TFunction, variant: AlbumVariant) =>
  variant === 'releases'
    ? {
        title: t('catalogs-management:releases', 'Releases'),
        description: t(
          'catalogs-management:releasesDescription',
          'Here you can choose which releases should appear on the Catalog and be featured on the home page. You can also add cover art for your releases.',
        ),
        publishedTab: t(
          'catalogs-management:publishedReleases',
          'Published releases',
        ),
        featuredTab: t(
          'catalogs-management:featuredReleases',
          'Featured releases',
        ),
        addToFeatured: t(
          'catalogs-management:addToFeaturedReleases',
          'Add to featured releases',
        ),
        removeFromFeatured: t(
          'catalogs-management:removeFromFeaturedReleases',
          'Remove from featured releases',
        ),
        edit: t('catalogs-management:editRelease', 'Edit release'),
        unpublish: t(
          'catalogs-management:unpublishThisRelease',
          'Unpublish this release',
        ),
        publishNew: t(
          'catalogs-management:publishNewRelease',
          'Publish new release',
        ),
        noFeatured: t(
          'catalogs-management:noFeaturedReleases',
          'No featured releases here yet! You can feature releases you’ve added to your Catalog in the “Published releases” tab.',
        ),
        noSearchResults: (addPath: string) => (
          <Trans t={t} i18nKey="catalogs-management:noReleasesFoundAddHere">
            Sorry! We couldn’t find anything for that search. Please try another
            query, or, if the release you’re looking for isn’t published yet,{' '}
            <InlineLink to={addPath}>add it here</InlineLink>.
          </Trans>
        ),
        addInstructions: t(
          'catalogs-management:addReleasesInstructions',
          'To add releases to your Catalog, browse them below or search for them, and click the checkbox.',
        ),
        publishCount: (count: number) =>
          t(
            'catalogs-management:publishCountReleases',
            'Publish {{count}} releases',
            {
              count,
              defaultValue_one: 'Publish {{count}} release',
            },
          ),
        publishAll: t(
          'catalogs-management:publishReleases',
          'Publish releases',
        ),
        options: t('catalogs-management:releaseOptions', 'Release options'),
        chooseDesignView: t(
          'catalogs-management:chooseDesignViewForRelease',
          'Choose design view for release',
        ),
        selectCoverColor: t(
          'catalogs-management:selectColorForReleaseCover',
          'Select a color for your release cover',
        ),
        selectCoverImage: t(
          'catalogs-management:selectImageForReleaseCover',
          'Select an image for your release cover',
        ),
      }
    : {
        title: t('catalogs-management:albums', 'Albums'),
        description: t(
          'catalogs-management:albumsDescription',
          'Here you can choose which albums should appear on the Catalog and be featured on the home page. You can also add cover art for your albums.',
        ),
        publishedTab: t(
          'catalogs-management:publishedAlbums',
          'Published albums',
        ),
        featuredTab: t('catalogs-management:featuredAlbums', 'Featured albums'),
        addToFeatured: t(
          'catalogs-management:addToFeaturedAlbums',
          'Add to featured albums',
        ),
        removeFromFeatured: t(
          'catalogs-management:removeFromFeaturedAlbums',
          'Remove from featured albums',
        ),
        edit: t('catalogs-management:editAlbum', 'Edit album'),
        unpublish: t(
          'catalogs-management:unpublishThisAlbum',
          'Unpublish this album',
        ),
        publishNew: t(
          'catalogs-management:publishNewAlbum',
          'Publish new album',
        ),
        noFeatured: t(
          'catalogs-management:noFeaturedAlbums',
          'No featured albums here yet! You can feature albums you’ve added to your Catalog in the “Published albums” tab.',
        ),
        noSearchResults: (addPath: string) => (
          <Trans t={t} i18nKey="catalogs-management:noAlbumsFoundAddHere">
            Sorry! We couldn’t find anything for that search. Please try another
            query, or, if the album you’re looking for isn’t published yet,{' '}
            <InlineLink to={addPath}>add it here</InlineLink>.
          </Trans>
        ),
        addInstructions: t(
          'catalogs-management:addAlbumsInstructions',
          'To add albums to your Catalog, browse them below or search for them, and click the checkbox.',
        ),
        publishCount: (count: number) =>
          t(
            'catalogs-management:publishCountAlbums',
            'Publish {{count}} albums',
            {
              count,
              defaultValue_one: 'Publish {{count}} album',
            },
          ),
        publishAll: t('catalogs-management:publishAlbums', 'Publish albums'),
        options: t('catalogs-management:albumOptions', 'Album options'),
        chooseDesignView: t(
          'catalogs-management:chooseDesignViewForAlbum',
          'Choose design view for album',
        ),
        selectCoverColor: t(
          'catalogs-management:selectColorForAlbumCover',
          'Select a color for your album cover',
        ),
        selectCoverImage: t(
          'catalogs-management:selectImageForAlbumCover',
          'Select an image for your album cover',
        ),
      }
